import { Component, OnDestroy, OnInit, ChangeDetectorRef, ViewChild } from '@angular/core';
import { ApiService } from '@app/core/services/api/api.service';
import { ActivatedRoute, Router } from '@angular/router';
import {
  DashboardContentOptionsBuilder,
  FrameOptionsBuilder,
} from '@app/services/quicksight-analytics/quicksight-analytics.service';
import { ShopService } from '@app/services/shops/shop.service';
import { Shop } from '@app/services/shops/shop.model';
import { createEmbeddingContext, DashboardExperience } from 'amazon-quicksight-embedding-sdk';
import { Subject, firstValueFrom } from 'rxjs';
import { map, switchMap, takeUntil } from 'rxjs/operators';
import {
  TAB_PARAMETERS,
  TabNames,
  CROSS_SITE_PARAMS,
  REGULAR_TEST_RESULTS_PARAMS,
  DASHBOARD_PARAM_DEFAULTS,
} from './constants';
import { extractDashboardParameters } from './query-params.utils';
import { UserService } from '@app/services/user/user.service';
import { PdqTabsComponent } from '@app/storybook/components/tabs-group/tabs/tabs.component';

interface TabConfig {
  id: string;
  name: string;
  active?: boolean;
}

interface QuickSightResult {
  url: string;
  sheets?: TabConfig[];
}

interface QuickSightParameterEvent {
  eventName: 'PARAMETERS_CHANGED';
  message: {
    changedParameters: {
      Name: string;
      Values: string[];
    }[];
  };
}

@Component({
  selector: 'pdq-labs',
  templateUrl: './pdq-labs.component.html',
  styleUrls: ['./pdq-labs.component.scss'],
  standalone: false,
})
export class PDQLabsComponent implements OnInit, OnDestroy {
  @ViewChild(PdqTabsComponent) tabsGroup: PdqTabsComponent;

  notFoundMsg: string | null = null;
  loading = true;
  tabs: TabConfig[] = [];
  embeddedExperience: DashboardExperience | null;
  currentShop: Shop;
  currentUrl: string;
  destroy$ = new Subject<void>();
  currentParameters: Record<string, string> = {};
  parameterListener?: (event: MessageEvent) => void;
  private blockAutoNavigationUntil = 0;
  private crossSiteSheetId: string | null = null; // Store the hidden cross-site sheet ID

  constructor(
    private apiService: ApiService,
    private userService: UserService,
    private shopService: ShopService,
    private route: ActivatedRoute,
    private router: Router,
    private cdr: ChangeDetectorRef,
  ) {}

  async ngOnInit(): Promise<void> {
    this.extractQueryParams();
    this.setupDashboard();
    this.setupParameterListener();
  }

  private extractQueryParams(): void {
    this.currentParameters = extractDashboardParameters(this.route.snapshot.queryParams);
  }

  private setupDashboard(): void {
    this.shopService.shop$
      .pipe(
        takeUntil(this.destroy$),
        switchMap((shop) =>
          this.apiService.get('quicksight/abtest-dashboard-embedded-url').pipe(map((result) => ({ shop, result }))),
        ),
      )
      .subscribe(({ shop, result }) => {
        if (!shop || !result?.url) {
          this.loading = false;
          this.notFoundMsg = 'Unable to load analytics dashboard';
          return;
        }

        this.currentShop = shop;
        this.currentUrl = result.url;
        this.setupTabs(result, shop);
        this.updateUrl(); // Replace sheetName with resolved tab ID in URL

        // Skip QuickSight initialization if native All Tests dashboard is showing
        if (this.shouldShowNativeAllTests) {
          this.loading = false;
        } else {
          this.initDashboard(result.url, shop);
        }
      });
  }

  private setupTabs(result: QuickSightResult, shop: Shop): void {
    const sheets = this.filterSheetsByConfig(result.sheets || [], shop);

    const crossSiteSheet = sheets.find(
      (sheet) => this.normalizeSheetName(sheet.name) === TabNames.TEST_RESULTS_CROSS_SITE,
    );
    if (crossSiteSheet) {
      this.crossSiteSheetId = crossSiteSheet.id;
    }

    const visibleSheets = sheets.filter(
      (sheet) => this.normalizeSheetName(sheet.name) !== TabNames.TEST_RESULTS_CROSS_SITE,
    );
    this.tabs = visibleSheets.map((sheet, i) => ({ ...sheet, active: i === 0 }));

    let initialTabId = this.currentParameters.tab;

    // Resolve sheetName to actual tab ID if provided
    if (this.currentParameters.sheetName && !initialTabId) {
      const matchedSheet = visibleSheets.find(
        (sheet) => this.normalizeSheetName(sheet.name) === this.currentParameters.sheetName,
      );
      if (matchedSheet) {
        initialTabId = matchedSheet.id;
      }
    }

    if (this.currentParameters.testName && !initialTabId) {
      const testResultsTab = visibleSheets.find(
        (sheet) => this.normalizeSheetName(sheet.name) === TabNames.TEST_RESULTS,
      );
      if (testResultsTab) {
        initialTabId = testResultsTab.id;
      }
    }

    if (initialTabId) {
      this.tabs.forEach((tab) => (tab.active = tab.id === initialTabId));
    }
  }

  private filterSheetsByConfig(sheets: TabConfig[], shop?: Shop): TabConfig[] {
    if (!sheets) return sheets;

    if (shop?.isEssentials) {
      return sheets.filter((sheet) => {
        const normalizedName = this.normalizeSheetName(sheet.name);
        return normalizedName === TabNames.CHECKOUT_PERFORMANCE || normalizedName === TabNames.SHIPPING_ANALYTICS;
      });
    }

    const { analyticsDashboardConfig, enableTrackingPage } = shop?.metadata || {};

    return sheets.filter((sheet) => {
      const normalizedName = this.normalizeSheetName(sheet.name);
      if (normalizedName === TabNames.SHIPPING_ANALYTICS) return !!enableTrackingPage;

      if (!analyticsDashboardConfig) return sheet;
      const sheetConfig = analyticsDashboardConfig[normalizedName];
      if (sheetConfig === undefined) return true;

      return sheetConfig;
    });
  }

  /**
   * Normalizes sheet names for config lookup
   * @param name - The original sheet name from QuickSight
   * @returns Normalized kebab-case name for config matching
   *
   * @example
   * Name transformation:
   * "Deep A/B Test Analysis" → "deep-a-b-test-analysis"
   *
   * Usage in analyticsDashboardConfig:
   * {
   *   "pdq-impact": false,
   *   "all-tests": false,
   *   "test-results": false,
   *   "deep-a-b-test-analysis": false,
   *   "checkout-performance": true,
   * }
   *
   */
  private normalizeSheetName(name: string): string {
    return name
      .toLowerCase()
      .replace(/[^a-z]/g, ' ')
      .replace(/\s+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  private isCrossSiteTest(): boolean {
    const hasCrossSiteTest =
      this.currentParameters.CrossSitetestTestName &&
      this.currentParameters.CrossSitetestTestName !== DASHBOARD_PARAM_DEFAULTS.NO_FILTER;
    const hasRegularTest =
      this.currentParameters.testName && this.currentParameters.testName !== DASHBOARD_PARAM_DEFAULTS.NO_FILTER;

    if (hasCrossSiteTest && !hasRegularTest) {
      return true;
    }

    const entryPoint = this.currentParameters.ShowCheckoutORCrossSiteAB?.toLowerCase() || '';
    if (entryPoint.includes('cross-site') || entryPoint.includes('cross site')) {
      return true;
    }

    return false;
  }

  private getSheetIdToLoad(activeTab: TabConfig): string {
    const isCrossSiteContent =
      this.isCrossSiteTest() &&
      this.normalizeSheetName(activeTab.name) === TabNames.TEST_RESULTS &&
      this.crossSiteSheetId;

    return isCrossSiteContent ? this.crossSiteSheetId : activeTab.id;
  }

  private async initDashboard(url: string, shop: Shop): Promise<void> {
    try {
      this.clearIframes();

      const context = await createEmbeddingContext();

      // Abort if native all-tests became active during async operation
      if (this.shouldShowNativeAllTests) return;

      const frameOptions = new FrameOptionsBuilder(url, '#pdq-labs-container')
        .withOnChange((event) => {
          if (event.eventName === 'FRAME_LOADED') {
            this.loading = false;
            this.styleIframe();
          }
        })
        .build();

      let contentOptions = new DashboardContentOptionsBuilder()
        .withParameter('shopId', shop.id.toString())
        .withParameter('shopName', shop.name)
        .withParameter('pdqAppUserEmail', this.userService.currentMeUser?.email || '')
        .withParameter('shopToken', shop.metadata.quicksightToken)
        .withParameter('DeliveryCountry', shop.address.countryCode)
        .withParameter('48States', shop.address.countryCode.toLowerCase() === 'us' ? '48 States' : 'ALL_VALUES')
        .withParameter('Currency', shop.metadata.currency)
        .withSheetTabsDisabled(true)
        .withToolbarOptions({ export: false, undoRedo: false, reset: false });

      Object.entries(this.currentParameters).forEach(([key, value]) => {
        if (key !== 'tab' && key !== 'sheetName' && value != null && value !== '') {
          contentOptions = contentOptions.withParameter(key, value);
        }
      });

      const activeTab = this.tabs.find((t) => t.active);
      if (activeTab) {
        const sheetIdToLoad = this.getSheetIdToLoad(activeTab);
        contentOptions = contentOptions.withSheetOptions(sheetIdToLoad, true);
      }

      // Abort if native all-tests became active while building options
      if (this.shouldShowNativeAllTests) return;

      this.embeddedExperience = await context.embedDashboard(frameOptions, contentOptions.build());
    } catch (error) {
      console.error('Failed to initialize dashboard:', error);
      this.loading = false;
    }
  }

  private clearIframes(): void {
    const container = document.getElementById('pdq-labs-container');
    container?.querySelectorAll('iframe').forEach((iframe) => iframe.remove());
    this.embeddedExperience = null;
  }

  private styleIframe(): void {
    const container = document.getElementById('pdq-labs-container');
    const iframe = container?.querySelector('iframe');
    if (iframe) iframe.style.border = '1px solid #e5e5e5';
  }

  private setupParameterListener(): void {
    if (this.parameterListener) {
      window.removeEventListener('message', this.parameterListener);
    }

    this.parameterListener = (event: MessageEvent) => {
      if (event.origin.includes('quicksight.aws.amazon.com') && event.data?.eventName === 'PARAMETERS_CHANGED') {
        this.handleParameterChange(event.data);
      }
    };

    window.addEventListener('message', this.parameterListener);
  }

  private handleParameterChange(data: QuickSightParameterEvent): void {
    const activeTab = this.tabs.find((t) => t.active);
    if (!activeTab || !data.message?.changedParameters) return;

    const allowedParams = this.getAllowedParams(activeTab);
    const isTestResultsTab = this.normalizeSheetName(activeTab.name) === TabNames.TEST_RESULTS;
    let updated = false;

    const currentTestType = isTestResultsTab ? (this.isCrossSiteTest() ? 'cross-site' : 'regular') : null;

    data.message.changedParameters.forEach((param) => {
      if (param?.Name && param?.Values?.[0] != null && allowedParams.includes(param.Name)) {
        if (isTestResultsTab && currentTestType) {
          const isCrossSiteParam = (CROSS_SITE_PARAMS as readonly string[]).includes(param.Name);
          const isRegularParam = (REGULAR_TEST_RESULTS_PARAMS as readonly string[]).includes(param.Name);

          if (currentTestType === 'cross-site' && isRegularParam) return;
          if (currentTestType === 'regular' && isCrossSiteParam) return;
        }

        this.currentParameters[param.Name] = String(param.Values[0]);
        updated = true;

        if (param.Name === 'testName' || param.Name === 'CrossSitetestTestName') {
          const normalizedTabName = this.normalizeSheetName(activeTab.name);

          if (
            normalizedTabName === TabNames.ALL_TESTS &&
            param.Values[0] &&
            param.Values[0] !== DASHBOARD_PARAM_DEFAULTS.NO_FILTER &&
            Date.now() > this.blockAutoNavigationUntil
          ) {
            this.handleTestSelection();
          }
        }
      }
    });

    if (updated) {
      this.updateUrl();
    }
  }

  private handleTestSelection(): void {
    const testResultsTab = this.tabs.find((tab) => this.normalizeSheetName(tab.name) === TabNames.TEST_RESULTS);

    if (testResultsTab) {
      this.setTab(testResultsTab.id);
    }
  }

  private getAllowedParams(tab: TabConfig): string[] {
    const normalized = this.normalizeSheetName(tab.name);
    return TAB_PARAMETERS[normalized] || [];
  }

  async setTab(selectedTabId: string): Promise<void> {
    try {
      const selectedTab = this.tabs.find((tab) => tab.id === selectedTabId);

      if (selectedTab && this.normalizeSheetName(selectedTab.name) === TabNames.ALL_TESTS) {
        this.blockAutoNavigationUntil = Date.now() + 1000;
        REGULAR_TEST_RESULTS_PARAMS.forEach((param) => {
          delete this.currentParameters[param];
        });
        CROSS_SITE_PARAMS.forEach((param) => {
          delete this.currentParameters[param];
        });
      }

      if (selectedTab && this.normalizeSheetName(selectedTab.name) === TabNames.TEST_RESULTS) {
        if (this.isCrossSiteTest()) {
          REGULAR_TEST_RESULTS_PARAMS.forEach((param) => {
            delete this.currentParameters[param];
          });
        } else {
          CROSS_SITE_PARAMS.forEach((param) => {
            delete this.currentParameters[param];
          });
        }
      }

      this.updateTabs(selectedTabId);
      this.cleanParametersForTab();
      this.updateUrl();
      this.cdr.detectChanges();

      if (this.tabsGroup) {
        setTimeout(() => this.tabsGroup.updateUnderline(), 0);
      }

      // Skip QuickSight sheet switch if native All Tests dashboard is active
      if (this.shouldShowNativeAllTests) {
        this.clearIframes();
      } else {
        await this.switchSheet(selectedTabId);
      }
    } catch (error) {
      console.error('Failed to switch tab:', error);
    }
  }

  private updateTabs(selectedId: string): void {
    this.tabs.forEach((tab) => (tab.active = tab.id === selectedId));
  }

  private cleanParametersForTab(): void {
    const activeTab = this.tabs.find((t) => t.active);
    if (!activeTab) return;

    let allowed = [...this.getAllowedParams(activeTab), 'tab'];

    if (this.normalizeSheetName(activeTab.name) === TabNames.TEST_RESULTS) {
      if (this.isCrossSiteTest()) {
        allowed = allowed.filter((param) => !(REGULAR_TEST_RESULTS_PARAMS as readonly string[]).includes(param));
      } else {
        allowed = allowed.filter((param) => !(CROSS_SITE_PARAMS as readonly string[]).includes(param));
      }
    }

    this.currentParameters = Object.fromEntries(
      Object.entries(this.currentParameters).filter(([key]) => allowed.includes(key)),
    );
  }

  private async switchSheet(sheetId: string): Promise<void> {
    try {
      const selectedTab = this.tabs.find((tab) => tab.id === sheetId);
      if (!selectedTab) return;

      const actualSheetId = this.getSheetIdToLoad(selectedTab);

      if (this.embeddedExperience?.setSelectedSheetId) {
        await this.embeddedExperience.setSelectedSheetId(actualSheetId);

        if (this.embeddedExperience?.setParameters) {
          const parameterOverrides = Object.entries(this.currentParameters)
            .filter(([key]) => key !== 'tab')
            .map(([Name, Values]) => ({ Name, Values: [Values] }));

          if (parameterOverrides.length > 0) {
            await this.embeddedExperience.setParameters(parameterOverrides);
          }
        }
      } else {
        await this.reinitDashboard();
      }
    } catch (error) {
      console.error('Failed to switch sheet:', error);
      await this.reinitDashboard();
    }
  }

  private async reinitDashboard(): Promise<void> {
    this.loading = true;
    this.cdr.detectChanges();

    const result: QuickSightResult = await firstValueFrom(
      this.apiService.get('quicksight/abtest-dashboard-embedded-url'),
    );

    if (result?.url) {
      this.currentUrl = result.url;
      await this.initDashboard(result.url, this.currentShop);
    } else {
      this.loading = false;
      this.notFoundMsg = 'Unable to load analytics dashboard';
    }
  }

  private getFilteredParameters(): Record<string, string> {
    const activeTab = this.tabs.find((t) => t.active);
    const params = { ...this.currentParameters };

    if (activeTab) params.tab = activeTab.id;

    // Remove sheetName from URL as it's only used for initial tab resolution
    delete params.sheetName;

    return Object.fromEntries(
      Object.entries(params).filter(
        ([_, value]) => value != null && value !== '' && (typeof value !== 'string' || value.trim() !== ''),
      ),
    );
  }

  private updateUrl(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: this.getFilteredParameters(),
      queryParamsHandling: 'replace',
    });
  }

  get isAllTestsTabActive(): boolean {
    const activeTab = this.tabs.find((t) => t.active);
    return activeTab ? this.normalizeSheetName(activeTab.name) === TabNames.ALL_TESTS : false;
  }

  get shouldShowNativeAllTests(): boolean {
    return this.isAllTestsTabActive;
  }

  handleNavigateToTestResults(event: {
    testName: string;
    isCrossSite: boolean;
    segmentWinnerClean?: string | null;
  }): void {
    CROSS_SITE_PARAMS.forEach((param) => {
      delete this.currentParameters[param];
    });
    REGULAR_TEST_RESULTS_PARAMS.forEach((param) => {
      delete this.currentParameters[param];
    });

    if (event.isCrossSite) {
      this.currentParameters.CrossSitetestTestName = event.testName;
    } else {
      this.currentParameters.testName = event.testName;
      if (event.segmentWinnerClean) {
        this.currentParameters.segmentparameter = event.segmentWinnerClean;
      }
    }

    const testResultsTab = this.tabs.find((tab) => this.normalizeSheetName(tab.name) === TabNames.TEST_RESULTS);

    if (testResultsTab) {
      this.setTab(testResultsTab.id);
    }
  }

  ngOnDestroy(): void {
    if (this.parameterListener) {
      window.removeEventListener('message', this.parameterListener);
    }
    this.destroy$.next();
    this.destroy$.complete();
  }
}
